import { safeColor } from "../../../../engine/core/validation.js";
import { mobileTodayButtonState } from "./today.js";
import { renderMarkdown } from "../../views/notes/markdown.js";
import { closeIcon, moveIcon, pencilIcon, targetIcon, trashIcon } from "../icons.js";
import { formatDateRange } from "../utils/dates.js";
import { escapeAttr, escapeHtml, escapeTextarea } from "../utils/html.js";
import { t } from "../../../../shared/i18n/index.js";
import { canOpenLinkedMap, canRequestLinkAccess, isInertLink, isNestedLink } from "../../../../engine/sync/link-state.js";

export function renderNodePanel(appState, deps) {
  const {
    callbacks,
    uiState,
    getBoardColumns,
    getNodeColorPalette,
    getNodeEditDraft,
    getNodeTagIds,
    getNodeTagLabels,
    getSafeStatus,
    getTagDefinitions,
    renderTagChip,
    renderToggleTag,
    stashUnsavedNodeEdit,
  } = deps;
  const panelNodeId = uiState.nodeEditing && uiState.editingNodeContext?.nodeId
    ? uiState.editingNodeContext.nodeId
    : uiState.activeNodeId;
  if (!panelNodeId) return "";
  const node = callbacks.Store.find(panelNodeId);
  if (!node) return "";

  const mapReadOnly = callbacks.Store.isReadOnly?.();
  // This node: also read-only in a linked map the user can only view; a link
  // showing no content here takes no name or children (desktop: same rules).
  const nodeReadOnly = mapReadOnly || !!callbacks.sharedCallbacks.isNodeReadOnly?.(node.id);
  const readOnly = nodeReadOnly || isInertLink(node);
  const isLink = !!node.linkedDocId;
  const nestedLink = isNestedLink(node);
  // Deleting (or unlinking) stays possible on a full map, to make room.
  const deleteBlocked = isLink
    ? callbacks.Store.isDeleteBlocked?.() || nestedLink
    : callbacks.Store.isDeleteBlocked?.() || !!callbacks.sharedCallbacks.isNodeDeleteBlocked?.(node.id) || isInertLink(node);
  const postponement = callbacks.sharedCallbacks.getTaskPostponement?.(node.id);
  const postponedStatus = postponement?.until
    ? `<p class="node-postponement-status" role="status">${escapeHtml(t("app.nodeModal.postponedUntil", { date: postponement.until }))}</p>` : '';

  const tagDefs = getTagDefinitions(node.id);
  const nodeTagIds = getNodeTagIds(node);
  const statusColumns = getBoardColumns(tagDefs);
  const statusTagIds = new Set(statusColumns.map((col) => col.tagId));
  const statusTags = statusColumns
    .map((col) => tagDefs.find((tag) => tag.id === col.tagId))
    .filter(Boolean);
  const regularTags = tagDefs.filter((tag) => !statusTagIds.has(tag.id));
  const closeLabel = escapeAttr(t("common.close"));
  const noTags = `<span class="mobile-muted">${escapeHtml(t("mobile.node.noTags"))}</span>`;

  if (uiState.nodeEditing && !readOnly) {
    // One-shot: if the user just accepted a recovery dialog, the recovered
    // payload overrides the saved values for this single render. Validate
    // the payload's docId/nodeId against the current edit context because a
    // doc-switch race between confirm-tap and this render would mismatch.
    let nameVal = node.name || "";
    let startVal = node.startDate || "";
    let endVal = node.endDate || "";
    let notesVal = node.notes || "";
    let colorVal = node.color || "";
    const draft = getNodeEditDraft();
    if (draft) {
      if (draft.name != null) nameVal = draft.name;
      if (draft.startDate != null) startVal = draft.startDate;
      if (draft.endDate != null) endVal = draft.endDate;
      if (draft.notes != null) notesVal = draft.notes;
      if (draft.color != null) colorVal = draft.color;
    }
    const r = uiState.recoveredEditPayload;
    if (r?.kind === "node") {
      const ctx = uiState.editingNodeContext;
      if (ctx && r.docId === ctx.docId && r.nodeId === ctx.nodeId && ctx.nodeId === node.id) {
        if (r.name != null) nameVal = r.name;
        if (r.startDate != null) startVal = r.startDate;
        if (r.endDate != null) endVal = r.endDate;
        if (r.notes != null) notesVal = r.notes;
        if (r.color != null) colorVal = r.color;
        uiState.nodeEditDraft = {
          docId: ctx.docId,
          nodeId: ctx.nodeId,
          name: nameVal,
          startDate: startVal,
          endDate: endVal,
          notes: notesVal,
          color: colorVal,
        };
        uiState.recoveredEditPayload = null;
      } else {
        // The stash was already consumed by the take in runConfirmAction.
        // Re-stash on mismatch so recovered text is not lost forever.
        stashUnsavedNodeEdit(r.docId, r.nodeId, {
          name: r.name, startDate: r.startDate, endDate: r.endDate, notes: r.notes, color: r.color,
        });
        uiState.recoveredEditPayload = null;
      }
    }
    const swatchHex = safeColor(colorVal, "#6b7280").toLowerCase();
    const palette = getNodeColorPalette ? getNodeColorPalette() : [];
    return `
      <div class="mobile-panel-backdrop" data-mobile-action="close-node"></div>
      <section role="dialog" aria-modal="true" class="mobile-panel mobile-panel-fullscreen mobile-node-panel">
        <header class="mobile-panel-header mobile-node-panel-header">
          <button class="mobile-icon-btn" data-mobile-action="close-node" aria-label="${closeLabel}">${closeIcon()}</button>
          <span class="mobile-status-dot ${getSafeStatus(node)}" style="--node-color:${safeColor(colorVal, "#6b7280")}"></span>
          <div class="mobile-panel-title mobile-node-panel-title">${escapeHtml(nameVal || t("common.untitled"))}</div>
          <button class="mobile-text-btn" data-mobile-action="save-node">${escapeHtml(t("common.save"))}</button>
        </header>
        <div class="mobile-panel-body">
          <label class="mobile-field">
            <span>${escapeHtml(t("common.name"))}</span>
            <input id="mobile-node-name" class="mobile-input-control" value="${escapeAttr(nameVal)}" />
          </label>
          ${statusTags.length ? `
            <div class="mobile-field">
              <span>${escapeHtml(t("mobile.node.status"))}</span>
              <div class="mobile-tag-picker mobile-status-picker">
                ${statusTags.map((tag) => renderToggleTag(tag, nodeTagIds.includes(tag.id), node.id, "status")).join("")}
              </div>
            </div>
          ` : ""}
          <div class="mobile-field">
            <span>${escapeHtml(t("common.tags"))}</span>
            <div class="mobile-tag-picker">
              ${regularTags.map((tag) => renderToggleTag(tag, nodeTagIds.includes(tag.id), node.id)).join("") || noTags}
            </div>
          </div>
          <section class="mobile-property-section" aria-label="${escapeAttr(t("html.node.planning"))}">
            <h3>${escapeHtml(t("html.node.planning"))}</h3>
            ${postponedStatus}
          <div class="mobile-field-grid">
            <label class="mobile-field">
              <span>${escapeHtml(t("mobile.node.start"))}</span>
              <input id="mobile-node-start" class="mobile-input-control" type="date" value="${escapeAttr(startVal)}" />
            </label>
            <label class="mobile-field">
              <span>${escapeHtml(t("mobile.node.end"))}</span>
              <input id="mobile-node-end" class="mobile-input-control" type="date" value="${escapeAttr(endVal)}" />
            </label>
          </div>
          <div class="mobile-date-shortcuts">
            <button data-mobile-action="set-node-date-preset" data-preset="today">${escapeHtml(t("common.today"))}</button>
            <button data-mobile-action="set-node-date-preset" data-preset="tomorrow">${escapeHtml(t("common.tomorrow"))}</button>
            <button data-mobile-action="set-node-date-preset" data-preset="week">${escapeHtml(t("mobile.node.plusWeek"))}</button>
            <button data-mobile-action="set-node-date-preset" data-preset="clear">${escapeHtml(t("common.clear"))}</button>
          </div>
          </section>
          <details class="mobile-property-section">
            <summary>${escapeHtml(t("mobile.appearance"))}</summary>
          <div class="mobile-field">
            <span>${escapeHtml(t("html.node.color"))}</span>
            <div class="mobile-color-swatches">
              ${palette.map((c) => `
                <button class="mobile-color-swatch ${c.toLowerCase() === swatchHex ? "selected" : ""}"
                        type="button"
                        data-mobile-action="set-node-color"
                        data-color="${escapeAttr(c)}"
                        style="background:${safeColor(c, "#6b7280")}"
                        aria-label="${escapeAttr(t("mobile.node.setColor", { color: c }))}"></button>
              `).join("")}
            </div>
            <span class="mobile-color-hint">${escapeHtml(t("mobile.node.colorHint"))}</span>
          </div>
          </details>
          <label class="mobile-field">
            <span>${escapeHtml(t("common.notes"))}</span>
            <textarea id="mobile-node-notes" class="mobile-input-control mobile-textarea-control">${escapeTextarea(notesVal)}</textarea>
          </label>
        </div>
      </section>
    `;
  }

  const notesHtml = node.notes
    ? renderMarkdown(String(node.notes), escapeHtml)
    : `<p class="mobile-muted">${escapeHtml(t("mobile.node.noNotes"))}</p>`;

  return `
    <div class="mobile-panel-backdrop" data-mobile-action="close-node"></div>
    <section role="dialog" aria-modal="true" class="mobile-panel mobile-panel-fullscreen mobile-node-panel">
      <header class="mobile-panel-header mobile-node-panel-header">
        <button class="mobile-icon-btn" data-mobile-action="close-node" aria-label="${closeLabel}">${closeIcon()}</button>
        <span class="mobile-status-dot ${getSafeStatus(node)}" style="--node-color:${safeColor(node.color, "#6b7280")}"></span>
        <div class="mobile-panel-title mobile-node-panel-title">${escapeHtml(node.name || t("common.untitled"))}</div>
        ${readOnly ? "" : `<button class="mobile-text-btn" data-mobile-action="edit-node">${escapeHtml(t("common.edit"))}</button>`}
      </header>
      <div class="mobile-panel-body">
        <section class="mobile-property-section" aria-label="${escapeAttr(t("mobile.node.statusTagsAria"))}">
          <h3>${escapeHtml(t("html.node.statusTags"))}</h3>
        ${readOnly ? `
          <div class="mobile-tag-row detail">
            ${getNodeTagLabels(node).map(renderTagChip).join("") || noTags}
          </div>
        ` : `
          ${statusTags.length ? `
            <div class="mobile-tag-picker mobile-status-picker detail">
              ${statusTags.map((tag) => renderToggleTag(tag, nodeTagIds.includes(tag.id), node.id, "status")).join("")}
            </div>
          ` : ""}
          <div class="mobile-tag-picker detail">
            ${regularTags.map((tag) => renderToggleTag(tag, nodeTagIds.includes(tag.id), node.id)).join("") || noTags}
          </div>
        `}
        </section>
        <section class="mobile-property-section" aria-label="${escapeAttr(t("mobile.node.execution"))}">
          <h3>${escapeHtml(t("mobile.node.execution"))}</h3>
          ${postponedStatus}
          <div class="mobile-detail-actions">
          ${(() => {
            if (readOnly || node.id === appState.data?.id) return "";
            const st = mobileTodayButtonState(node.id, deps);
            if (!st || !st.noteId) return "";
            return `<button class="mobile-secondary-btn${st.on ? " on" : ""}" data-mobile-action="today-toggle-node" data-node-id="${escapeAttr(node.id)}" aria-pressed="${st.on}" title="${escapeAttr(st.on ? t("mobile.node.listRemoveTitle") : t("mobile.node.listAddTitle"))}"><span>${escapeHtml(st.on ? t("mobile.node.listed") : t("mobile.node.list"))}</span></button>`;
          })()}

          ${!readOnly && callbacks.sharedCallbacks.canPostponeTask?.(node.id) ? `<button class="mobile-secondary-btn" data-task-postpone="${escapeAttr(node.id)}">${escapeHtml(postponement?.until ? t("app.nodeModal.changeReturnDate") : t("app.nodeModal.postpone"))}</button>` : ''}
          </div>
          ${formatDateRange(node) ? `<p class="mobile-muted">${escapeHtml(t("mobile.node.planned", { range: formatDateRange(node) }))}</p>` : ''}
        </section>
        ${readOnly ? '' : `<section class="mobile-property-section" aria-label="${escapeAttr(t("views.name.routines"))}">
          <h3>${escapeHtml(t("views.name.routines"))}</h3>
          <div class="mobile-detail-actions"><button class="mobile-secondary-btn" data-routine-configure="${escapeAttr(node.id)}">${escapeHtml(t("mobile.node.repeatRoutine"))}</button><button class="mobile-secondary-btn" data-routine-capture="${escapeAttr(node.id)}">${escapeHtml(t("mobile.node.addToRoutine"))}</button></div>
        </section>`}
        <section class="mobile-property-section" aria-label="${escapeAttr(t("mobile.node.actions"))}">
          <h3>${escapeHtml(t("mobile.node.heading"))}</h3>
          <div class="mobile-detail-actions">
          <button class="mobile-secondary-btn" data-mobile-action="related-notes" data-node-id="${escapeAttr(node.id)}">${escapeHtml(t("app.menu.relatedNotes"))}</button>
          <button class="mobile-secondary-btn" data-mobile-action="focus-node" data-node-id="${escapeAttr(node.id)}">${targetIcon()}<span>${escapeHtml(t("mobile.node.focus"))}</span></button>
          ${readOnly ? "" : `<button class="mobile-secondary-btn" data-mobile-action="open-rename-node-form" data-node-id="${escapeAttr(node.id)}">${pencilIcon()}<span>${escapeHtml(t("common.rename"))}</span></button>`}
          ${(isLink ? mapReadOnly || nestedLink : readOnly) || node.id === appState.data?.id ? "" : `<button class="mobile-secondary-btn" data-mobile-action="open-move-node" data-node-id="${escapeAttr(node.id)}">${moveIcon()}<span>${escapeHtml(t("mobile.node.move"))}</span></button>`}
          ${canOpenLinkedMap(node) ? `<button class="mobile-secondary-btn" data-mobile-action="link-badge" data-node-id="${escapeAttr(node.id)}"><span>${escapeHtml(t("app.menu.openDocument"))}</span></button>` : ""}
          ${canRequestLinkAccess(node) ? `<button class="mobile-secondary-btn" data-mobile-action="link-badge" data-node-id="${escapeAttr(node.id)}"><span>${escapeHtml(t("app.requestAccess.title"))}</span></button>` : ""}
          </div>
          ${node.children?.length ? `<p class="mobile-muted">${escapeHtml(t("mobile.node.childCount", { count: node.children.length }))}</p>` : ''}
        </section>
        <h3 class="mobile-property-heading">${escapeHtml(t("common.notes"))}</h3>
        <section class="mobile-notes-rendered">
          ${notesHtml}
        </section>
        ${deleteBlocked || node.id === appState.data?.id ? "" : `
          <div class="mobile-danger-zone">
            <button class="mobile-danger-icon-btn"
                    data-mobile-action="request-delete-node"
                    data-node-id="${escapeAttr(node.id)}"
                    aria-label="${escapeAttr(t(isLink ? "app.menu.unlinkDocument" : "mobile.node.delete"))}"
                    title="${escapeAttr(t(isLink ? "app.menu.unlinkDocument" : "mobile.node.delete"))}">${trashIcon()}</button>
          </div>
        `}
      </div>
    </section>
  `;
}
